/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { renderUserTag } from "@CubeComponents/html/userTagDom";
import type { SpaceUserItem } from "@ServerTypes/spaces/space.schema";

interface AddAssignedToUsersListProps {
  users: SpaceUserItem[];
  closeModal: () => void;
  excludedUsers?: string[];
  handleNext: (selectedUsers: SpaceUserItem[]) => void;
}

function AddAssignedToUsersList({ users, closeModal, excludedUsers, handleNext }: AddAssignedToUsersListProps) {
  const { t } = useTranslation();
  const dataGrid = useRef<any>(null);
  const [addUsersModalVisible, setAddUsersModalVisible] = useState(true);
  const [addEnabled, setAddEnabled] = useState(false);
  const [dataSource, setDataSource] = useState<SpaceUserItem[]>([]);

  useEffect(() => {
    setDataSource(users.filter((user) => !excludedUsers?.includes(user.userId)));
  }, [users, excludedUsers]);

  const handleCancelAddUsersModal = () => {
    closeModal();
  };

  const GridRowSelectionHandler = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current?.dataView?.getAllSelectedItems() || [];
      if (selectedRecords.length) {
        setAddEnabled(true);
      } else {
        setAddEnabled(false);
      }
    }
  };

  const fullNameFormatter = (row, cell, value, columnDef, dataContext) => {
    const container = document.createElement("div");
    renderUserTag(container, { id: dataContext.userId });
    return container;
  };

  const handleNextClick = async () => {
    const selectedUsers = dataGrid.current?.dataView?.getAllSelectedItems() || [];
    handleNext(selectedUsers);
    setAddUsersModalVisible(false);
    closeModal();
  };

  const addUserModalHeader = () => (
    <div className="flex w-full justify-between" data-testid="addUserModalHeader">
      <span className="text-[1.75rem] font-bold text-[var(--primary-text)]" data-testid="selectUsersToSendMail">
        {t("Select Users")}
      </span>
    </div>
  );

  const columnDefinitions = useMemo(
    () => [
      {
        // SpaceUserItem carries the display name in `userName`; the formatter renders the
        // avatar from dataContext.userId, but filter/search/sort must target the real field.
        id: "userName",
        field: "userName",
        name: t("User Name"),
        minWidth: 150,
        formatter: fullNameFormatter,
      },
      {
        id: "email",
        field: "email",
        name: t("Email"),
        minWidth: 200,
        clipMode: "EllipsisWithTooltip",
      },
      {
        id: "organisation",
        field: "organisation",
        name: t("Org"),
        minWidth: 150,
        clipMode: "EllipsisWithTooltip",
      },
      {
        id: "designation",
        field: "designation",
        name: t("Designation"),
        minWidth: 150,
        clipMode: "EllipsisWithTooltip",
      },
    ],
    [],
  );

  const GridDataList = useCallback(
    () => (
      <SlickGrid1
        ref={dataGrid}
        dataset={dataSource}
        columnDefinitions={columnDefinitions as any}
        gridHeight="100%"
        rowHeight={60}
        onSelectedRowIdsChanged={GridRowSelectionHandler}
        enableSearchBar
        enableFilteringOption
        enableSortingOption
        infiniteScroll
        data-testid="allUsersGridList"
      />
    ),

    [dataSource],
  );

  const addUserModalContent = () => <div className="h-100">{GridDataList()}</div>;

  const addUserModal = () => (
    <ModalStateDialogComponent
      width="80%"
      allowModalPadding={false}
      headerTitle={addUserModalHeader()}
      modelContent={addUserModalContent()}
      onCancel={handleCancelAddUsersModal}
      footerContent={
        <div className="flex gap-[1.375rem]">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={handleNextClick}
            disabled={!addEnabled}
            data-testid="nextAddUserBtn"
          >
            {t("Add")}
          </ButtonComponent>
          <ButtonComponent
            buttonType={BUTTON_TYPES.BORDER_BUTTON}
            onClick={handleCancelAddUsersModal}
            data-testid="cancelAddUserBtn"
          >
            {t("Discard")}
          </ButtonComponent>
        </div>
      }
      isVisible={addUsersModalVisible}
    />
  );

  return <div>{addUserModal()}</div>;
}

export default AddAssignedToUsersList;
